import { useEffect, useState } from '../lib/teact/teact';

import { ApiMediaFormat } from '../api/types';

import { selectIsSynced } from '../global/selectors';
import {
  APP_WAKE_EVENT,
  isProgressiveActive,
} from '../util/browser/windowEnvironment';
import * as mediaLoader from '../util/mediaLoader';
import useSelector from './data/useSelector';
import useForceUpdate from './useForceUpdate';

const useMedia = (
  mediaHash: string | false | undefined,
  noLoad = false,
  mediaFormat = ApiMediaFormat.BlobUrl,
  delay?: number | false,
  cacheBuster?: number,
) => {
  // Progressive only when the SW is actually controlling the page; otherwise fall back to the blob
  // path (works without the SW) so audio/video play on a cold load instead of failing until a reload.
  const isStreaming =
    isProgressiveActive() && mediaFormat === ApiMediaFormat.Progressive;
  // Prefer an already-downloaded blob (the fallback path caches one) so a clip that is already playing
  // is never swapped to a `/progressive/` URL if the SW later takes control.
  const fromMemory = mediaHash
    ? mediaLoader.getFromMemory(mediaHash)
    : undefined;
  const mediaData = mediaHash
    ? (fromMemory ??
      (isStreaming ? mediaLoader.getProgressiveUrl(mediaHash) : undefined))
    : undefined;

  const forceUpdate = useForceUpdate();
  const isSynced = useSelector(selectIsSynced);
  const [wakeVersion, setWakeVersion] = useState(0);

  // Nothing re-renders when the media cache evicts an entry, so a component keeps handing out the blob
  // URL it read on an earlier render long after that URL was revoked — the GIF picker, for one, would
  // pass a dead URL into the message it sends, which then renders a black frame with no way to recover.
  // Re-render on the revoke so the value below is read (and re-fetched) again.
  useEffect(() => {
    if (typeof mediaData !== 'string' || !mediaData.startsWith('blob:')) {
      return undefined;
    }

    return mediaLoader.onBlobUrlRevoked((url) => {
      if (url === mediaData) {
        forceUpdate();
      }
    });
  }, [mediaData, forceUpdate]);

  useEffect(() => {
    const handleWake = () => {
      setWakeVersion((version) => version + 1);
    };

    window.addEventListener(APP_WAKE_EVENT, handleWake);

    return () => {
      window.removeEventListener(APP_WAKE_EVENT, handleWake);
    };
  }, []);

  useEffect(() => {
    if (!noLoad && mediaHash && !mediaData) {
      const startedAt = Date.now();

      mediaLoader.fetch(mediaHash, mediaFormat).then(() => {
        const spentTime = Date.now() - startedAt;
        if (!delay || spentTime >= delay) {
          forceUpdate();
        } else {
          setTimeout(forceUpdate, delay - spentTime);
        }
      });
    }
  }, [
    noLoad,
    mediaHash,
    mediaData,
    mediaFormat,
    cacheBuster,
    delay,
    isSynced,
    wakeVersion,
  ]);

  return mediaData;
};

export default useMedia;
